import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { ensureGuestId } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ensureGuestId();
  }, []);

  const refresh = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check —
    // AuthCallback handles session_id exchange and establishes the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return null;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loginWithEmail = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("dpc_jwt", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const signupWithEmail = async (email, password, name) => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref") || localStorage.getItem("dpc_ref") || null;
    if (ref) localStorage.setItem("dpc_ref", ref);
    const res = await api.post("/auth/signup", { email, password, name, ref });
    localStorage.setItem("dpc_jwt", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithFacebook = () => {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID || "";
    const redirectUri = `${window.location.origin}/auth/facebook/callback`;
    if (!appId) {
      // No client-side app id configured yet — surface a clear message via a
      // toast at the call site by throwing.
      throw new Error("facebook_not_configured");
    }
    const state = crypto.randomUUID();
    sessionStorage.setItem("fb_oauth_state", state);
    const url =
      `https://www.facebook.com/v19.0/dialog/oauth` +
      `?client_id=${encodeURIComponent(appId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=email,public_profile` +
      `&state=${encodeURIComponent(state)}`;
    window.location.href = url;
  };

  const exchangeFacebookCode = async (code) => {
    const res = await api.post("/auth/facebook", {
      code,
      redirect_uri: `${window.location.origin}/auth/facebook/callback`,
    });
    localStorage.setItem("dpc_jwt", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const exchangeSessionId = async (sessionId) => {
    const res = await api.post("/auth/session", { session_id: sessionId });
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* noop */
    }
    localStorage.removeItem("dpc_jwt");
    setUser(null);
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      isPro: !!user?.is_subscribed || user?.subscription_status === "active",
      isSubscribed: !!user?.is_subscribed,
      planType: user?.plan_type || null,
      loginWithEmail,
      signupWithEmail,
      loginWithGoogle,
      loginWithFacebook,
      exchangeFacebookCode,
      exchangeSessionId,
      logout,
      refresh,
    }),
    [user, loading, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
